import {Inject, injectable} from 'inversify-props';
import {EMatchPolicy, EventDetails, WampDict, WampList} from '@verkehrsministerium/kraftfahrstrasse';

import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {Event} from '@/model/Event';

import {IEventsService} from '@/services/IEvents.service';
import {EventSubscriptions} from '@/services/Events.types';

import {MessagingStore} from '@/store/Messaging.store';

export type EventReceivedCallback = (event: Event) => Promise<void>;

@injectable()
export class EventsService implements IEventsService {
  private logger: ILogger = new LogdownLogger('EventsService');

  @Inject(Symbol.for('MessagingStore'))
  private messagingStore!: MessagingStore;

  private eventsSubscriptions: Map<string, EventSubscriptions> = new Map<string, EventSubscriptions>();

  private subscriptionCounter: number = 0;

  public async subscribe(eventType: string, callback: EventReceivedCallback): Promise<number> {
    const id = this.subscriptionCounter++;

    this.logger.trace(`Subscribing event '${eventType}', new id: ${id}`);

    let eventSubscriptions = this.eventsSubscriptions.get(eventType);

    if (!eventSubscriptions) {

      this.logger.debug(`Creating new subscription for event '${eventType}'`);
      const messagingSubscription = await this.messagingStore.subscribe(
        eventType,
        this.onMessageReceivedHandler.bind(this),
        EMatchPolicy.Exact,
      );

      eventSubscriptions = new EventSubscriptions(
        new Map<number, EventReceivedCallback>(),
        messagingSubscription,
      );

      this.eventsSubscriptions.set(eventType, eventSubscriptions);
    }

    eventSubscriptions.Callbacks.set(id, callback);
    this.logger.debug(`New callback (id=${id}) added to event '${eventType}'`);

    return id;
  }

  public async unsubscribe(id: number): Promise<void> {
    this.logger.trace(`Unsubscribing '${id}'`);

    for (const [eventType, eventSubscriptions] of this.eventsSubscriptions) {

      if (!eventSubscriptions.Callbacks.has(id)) {
        continue;
      }

      eventSubscriptions.Callbacks.delete(id);
      this.logger.debug(`Callback (id=${id}) removed for event '${eventType}'`);

      if (eventSubscriptions.Callbacks.size === 0) {
        this.logger.debug(`Event '${eventType}' has no more listeners, unsubscribing`);
        await this.messagingStore.unsubscribe(eventSubscriptions.MessagingSubscription);
        this.eventsSubscriptions.delete(eventType);
        this.logger.debug(`Event '${eventType}' has been definitely removed`);
      }
    }
  }

  private onMessageReceivedHandler(args: WampList, kwArgs: WampDict, details: EventDetails) {
    const event: Event = JSON.parse(args[0]);

    this.logger.trace(`Message received`, event);

    if (event.Type === undefined) {
      this.logger.warn(`No event type defined, ignoring message`, event);
      return;
    }

    const subscription = this.eventsSubscriptions.get(event.Type);

    if (subscription !== undefined) {
      for (const [, callback] of subscription.Callbacks) {
        callback(event).catch((error: Error) => {
          this.logger.error(`Event callback error`, error);
        });
      }
    } else {
      this.logger.warn(`No subscription found for this topic`, event);
    }
  }
}
